import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Signal, ChevronDown, ChevronUp, RefreshCw, UserCheck, Clock, Gauge, AlertTriangle, ArrowDown, ArrowUp, Minus, ArrowUpDown, Radio } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Listener Signal Quality Dashboard
 * ─────────────────────────────────
 * Shows which extra fields each station's provider returns:
 *   • current listeners  (always — baseline)
 *   • unique listeners   (Shoutcast v2 only)
 *   • average session    (Shoutcast v2 only)
 *   • server capacity    (Shoutcast v2 only)
 *
 * Helps user identify which stations give richer audience data
 * and which need enhancement (fingerprint / multi-url / provider upgrade).
 */

const QualityBadge = ({ score }) => {
  const cfg = score === 4
    ? { label: 'Full', cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' }
    : score === 3
    ? { label: 'High', cls: 'bg-sky-500/20 text-sky-400 border-sky-500/40' }
    : score === 2
    ? { label: 'Mid',  cls: 'bg-amber-500/20 text-amber-400 border-amber-500/40' }
    : score === 1
    ? { label: 'Low',  cls: 'bg-orange-500/20 text-orange-400 border-orange-500/40' }
    : { label: 'None', cls: 'bg-red-500/20 text-red-400 border-red-500/40' };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium ${cfg.cls}`}>
      {score}/4 {cfg.label}
    </span>
  );
};

const Dot = ({ on }) => (
  <span className={`inline-block w-2 h-2 rounded-full ${on ? 'bg-emerald-500' : 'bg-gray-600'}`} />
);

const formatDuration = (sec) => {
  if (!sec || sec <= 0) return '—';
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const SourceBadge = ({ type }) => {
  if (!type) return <span className="text-gray-500 text-[10px]">—</span>;
  const short = type === 'shoutcast_v2_json' ? 'Shoutcast v2'
             : type === 'icecast_json' ? 'Icecast'
             : type;
  const cls = type === 'shoutcast_v2_json'
    ? 'bg-purple-500/15 text-purple-400 border-purple-500/30'
    : 'bg-sky-500/15 text-sky-400 border-sky-500/30';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] ${cls}`}>
      {short}
    </span>
  );
};

/**
 * Shows quality change vs yesterday:
 *   null  → no baseline (first day)
 *   0     → unchanged (thin line)
 *   > 0   → improvement (green up arrow)
 *   < 0   → regression  (red down arrow + alert)
 */
const DeltaCell = ({ delta, regression }) => {
  if (delta == null) {
    return <span className="text-gray-600 text-[10px]" title="Χωρίς baseline ακόμη">—</span>;
  }
  if (delta === 0) {
    return <Minus className="w-3.5 h-3.5 text-gray-500 mx-auto" />;
  }
  if (regression) {
    return (
      <span className="inline-flex items-center gap-0.5 text-red-500 font-bold text-[11px]" title={`Απώλεια ${-delta} πεδίου signal από χθες`}>
        <ArrowDown className="w-3 h-3" /> {delta}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-emerald-500 text-[11px]" title={`Βελτίωση κατά ${delta} πεδίο(α) από χθες`}>
      <ArrowUp className="w-3 h-3" /> +{delta}
    </span>
  );
};

const ListenerSignalQuality = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  // Sort state: { key: columnKey, dir: 'asc' | 'desc' } or null for backend default
  const [sort, setSort] = useState(null);

  const fetchQuality = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/diagnostic/listener-signal-quality`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setLastUpdate(new Date());
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchQuality();
    const iv = setInterval(fetchQuality, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(iv);
  }, [fetchQuality]);

  // ── Sorted station list (hook must run unconditionally, pre-return) ──
  const sortedStations = useMemo(() => {
    const stations = data?.stations || [];
    if (!sort) return stations;
    const { key, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;
    const arr = [...stations];
    arr.sort((a, b) => {
      let va = a[key];
      let vb = b[key];
      if (key === 'name' || key === 'source_type') {
        va = (va || '').toString().toLocaleLowerCase('el-GR');
        vb = (vb || '').toString().toLocaleLowerCase('el-GR');
        return va.localeCompare(vb, 'el-GR') * mult;
      }
      // Numeric: nulls always at the bottom regardless of direction
      const aN = va == null ? null : Number(va);
      const bN = vb == null ? null : Number(vb);
      if (aN == null && bN == null) return 0;
      if (aN == null) return 1;
      if (bN == null) return -1;
      return (aN - bN) * mult;
    });
    return arr;
  }, [data, sort]);

  if (loading || !data) {
    return (
      <div
        className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 animate-pulse"
        data-testid="listener-signal-quality-loading"
      >
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48" />
      </div>
    );
  }

  const { totals } = data;
  const fullPct = totals.total_stations
    ? Math.round(100 * totals.full_signal / totals.total_stations)
    : 0;

  // ── Sort handling ─────────────────────────────────────────────
  // Click a header once → sort asc (or desc for numeric metrics for usefulness);
  // second click toggles direction; third click clears back to backend default.
  const NUMERIC_DESC_FIRST = new Set([
    'live_now', 'current_peak', 'current_avg', 'unique_peak', 'unique_avg',
    'avg_session_seconds', 'capacity_pct', 'quality_score', 'quality_delta',
  ]);
  const handleSort = (key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) {
        return { key, dir: NUMERIC_DESC_FIRST.has(key) ? 'desc' : 'asc' };
      }
      if (prev.dir === (NUMERIC_DESC_FIRST.has(key) ? 'desc' : 'asc')) {
        return { key, dir: NUMERIC_DESC_FIRST.has(key) ? 'asc' : 'desc' };
      }
      return null; // clear sort → backend default
    });
  };

  const SortHeader = ({ colKey, children, align = 'center', title, testId }) => {
    const active = sort?.key === colKey;
    const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
    const alignCls = align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center';
    return (
      <th
        className={`font-medium px-2 py-2 select-none ${alignCls}`}
        title={title}
      >
        <button
          type="button"
          data-testid={testId}
          onClick={() => handleSort(colKey)}
          aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
          className={`inline-flex items-center gap-1 px-1 py-0.5 rounded transition-colors cursor-pointer ${
            active
              ? 'text-purple-300 bg-purple-500/10'
              : 'hover:text-gray-200 hover:bg-white/5'
          }`}
        >
          <span>{children}</span>
          <Icon className={`w-3 h-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
        </button>
      </th>
    );
  };

  return (
    <div
      className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
      data-testid="listener-signal-quality"
    >
      {/* Summary Bar */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${totals.regressions > 0 ? 'bg-red-500/5' : ''}`}
        data-testid="listener-signal-quality-toggle"
      >
        <div className="flex items-center gap-4 text-sm flex-wrap">
          <div className="flex items-center gap-1.5 font-medium text-gray-700 dark:text-gray-300">
            <Signal className="w-4 h-4 text-purple-500" />
            <span>Listener Signal Quality</span>
            {/* Alert badge: regressions detected against yesterday */}
            {totals.regressions > 0 && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/15 border border-red-500/40 text-red-500 text-[10px] font-bold animate-pulse ml-1"
                title={`${totals.regressions} σταθμοί έχασαν signal από χθες`}
                data-testid="lsq-regression-badge"
              >
                <AlertTriangle className="w-3 h-3" />
                {totals.regressions} regression{totals.regressions > 1 ? 's' : ''}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400" title="Πλήρη σήματα (4/4)">
              <Dot on /> Full: {totals.full_signal}
            </span>
            <span className="flex items-center gap-1 text-red-600 dark:text-red-400" title="Χωρίς σήμα">
              <Dot /> None: {totals.no_signal}
            </span>
          </div>

          <span className="text-gray-500 dark:text-gray-400 hidden sm:inline">|</span>

          <span className="hidden sm:flex items-center gap-1 text-gray-600 dark:text-gray-400" title="Με unique listeners">
            <UserCheck className="w-3.5 h-3.5" /> Unique: {totals.with_unique}
          </span>
          <span className="hidden sm:flex items-center gap-1 text-gray-600 dark:text-gray-400" title="Με avg session time">
            <Clock className="w-3.5 h-3.5" /> Session: {totals.with_avg_time}
          </span>
          <span className="hidden md:flex items-center gap-1 text-gray-600 dark:text-gray-400" title="Με server capacity">
            <Gauge className="w-3.5 h-3.5" /> Capacity: {totals.with_max}
          </span>
        </div>

        <div className="flex items-center gap-2 text-gray-400">
          <span className="text-xs hidden md:inline text-purple-400 font-medium">{fullPct}% full</span>
          {lastUpdate && (
            <span className="text-xs hidden md:inline">
              {lastUpdate.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <RefreshCw
            className="w-3.5 h-3.5 cursor-pointer hover:text-purple-500 transition-colors"
            onClick={(e) => { e.stopPropagation(); fetchQuality(); }}
          />
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {/* Expanded Table */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 overflow-x-auto" data-testid="listener-signal-quality-table">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400">
              <tr>
                <SortHeader colKey="name" align="left" testId="lsq-sort-name">Σταθμός</SortHeader>
                <SortHeader colKey="quality_score" testId="lsq-sort-quality">Quality</SortHeader>
                <SortHeader colKey="quality_delta" title="Μεταβολή vs χθες" testId="lsq-sort-delta">Δ</SortHeader>
                <SortHeader colKey="source_type" testId="lsq-sort-provider">Provider</SortHeader>
                <SortHeader colKey="live_now" title="Live ακροατές αυτή τη στιγμή" testId="lsq-sort-now">
                  <span className="inline-flex items-center gap-1"><Radio className="w-3 h-3" />Now</span>
                </SortHeader>
                <SortHeader colKey="has_current" title="Current listeners" testId="lsq-sort-curr">Curr</SortHeader>
                <SortHeader colKey="has_unique" title="Unique IPs" testId="lsq-sort-uniq-flag">Uniq</SortHeader>
                <SortHeader colKey="has_avg_time" title="Avg session time" testId="lsq-sort-avglt-flag">Avg LT</SortHeader>
                <SortHeader colKey="has_max" title="Server max capacity" testId="lsq-sort-max-flag">Max</SortHeader>
                <SortHeader colKey="current_peak" align="right" title="Peak listeners (24h)" testId="lsq-sort-peak">Peak</SortHeader>
                <SortHeader colKey="current_avg" align="right" title="Average listeners (24h)" testId="lsq-sort-avg">Avg</SortHeader>
                <SortHeader colKey="unique_peak" align="right" title="Unique listeners peak (Shoutcast v2)" testId="lsq-sort-uniqpeak">Uniq</SortHeader>
                <SortHeader colKey="avg_session_seconds" align="right" title="Avg listening time" testId="lsq-sort-lt">LT</SortHeader>
                <SortHeader colKey="capacity_pct" align="right" title="Capacity utilization" testId="lsq-sort-cap">Cap%</SortHeader>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700/50">
              {sortedStations.map((s) => (
                <tr
                  key={s.id}
                  className={`hover:bg-gray-100/50 dark:hover:bg-gray-800/40 ${!s.active ? 'opacity-50' : ''} ${s.regression_detected ? 'bg-red-500/5' : ''}`}
                  data-testid={`lsq-row-${s.id}`}
                >
                  <td className="px-3 py-1.5">
                    {s.regression_detected && (
                      <AlertTriangle
                        className="w-3 h-3 text-red-500 inline-block mr-1 align-middle"
                        title="Απώλεια signal από χθες"
                      />
                    )}
                    <span className="font-medium text-gray-700 dark:text-gray-200 truncate max-w-[220px] inline-block align-middle">
                      {s.name}
                    </span>
                    {!s.active && <span className="ml-1.5 text-[9px] text-gray-500">inactive</span>}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <QualityBadge score={s.quality_score} />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <DeltaCell delta={s.quality_delta} regression={s.regression_detected} />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <SourceBadge type={s.source_type} />
                  </td>
                  {/* Now: live listeners (in-memory latest sample) */}
                  <td
                    className="px-2 py-1.5 text-center tabular-nums"
                    data-testid={`lsq-now-${s.id}`}
                    title={
                      s.live_now == null
                        ? 'Δεν υπάρχει πρόσφατο δείγμα'
                        : s.live_now_stale
                        ? `Stale (${s.live_now_age_sec}s πριν)`
                        : `Πρόσφατο (${s.live_now_age_sec ?? '?'}s πριν)`
                    }
                  >
                    {s.live_now == null ? (
                      <span className="text-gray-500">—</span>
                    ) : (
                      <span
                        className={`inline-flex items-center gap-1 font-semibold ${
                          s.live_now_stale ? 'text-gray-500' : 'text-emerald-400'
                        }`}
                      >
                        {!s.live_now_stale && (
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        )}
                        {s.live_now}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-center"><Dot on={s.has_current} /></td>
                  <td className="px-2 py-1.5 text-center"><Dot on={s.has_unique} /></td>
                  <td className="px-2 py-1.5 text-center"><Dot on={s.has_avg_time} /></td>
                  <td className="px-2 py-1.5 text-center"><Dot on={s.has_max} /></td>
                  {/* Split numeric columns — each right-aligned for clean scanning */}
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-300">
                    {s.current_peak ?? <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">
                    {s.current_avg ?? <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">
                    {s.unique_peak ?? <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">
                    {formatDuration(s.avg_session_seconds)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">
                    {s.capacity_pct != null ? `${s.capacity_pct}%` : <span className="text-gray-600">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="px-3 py-2 text-[10px] text-gray-400 dark:text-gray-500 border-t border-gray-200 dark:border-gray-700/50 flex gap-3 flex-wrap">
            <span>• <b>Now</b>: live ακροατές (τελευταίο sample)</span>
            <span>• <b>Curr</b>: current listeners (όλοι οι πάροχοι)</span>
            <span>• <b>Uniq</b>: unique IPs (Shoutcast v2)</span>
            <span>• <b>Avg LT</b>: μέσος χρόνος ακρόασης</span>
            <span>• <b>Max</b>: server capacity</span>
            <span>• <b>Cap%</b>: peak/capacity</span>
            <span>• <b>Δ</b>: μεταβολή quality vs χθες ({totals.has_baseline ? '✓ baseline ready' : '⏳ χτίζει baseline...'})</span>
            <span className="text-purple-400">• Κλικ σε header για ταξινόμηση</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListenerSignalQuality;
